import { Link } from 'react-router-dom';
import { ArrowRight, Star, Quote } from 'lucide-react';

const HERO_IMAGE = "/images/hero-car.jpg";
const DETAILING_IMAGE = "/images/detailing.jpg";
const INTERIOR_IMAGE = "/images/interior.jpg";
const GALLERY_IMAGE = "/images/gallery-car.jpg";

const Gallery = () => {
  const galleryImages = [
    { url: HERO_IMAGE, title: 'Premium Exterior Finish' },
    { url: DETAILING_IMAGE, title: 'Professional Polishing' },
    { url: INTERIOR_IMAGE, title: 'Interior Detailing' },
    { url: GALLERY_IMAGE, title: 'Luxury Car Care' },
    { url: HERO_IMAGE, title: 'Showroom Quality' },
    { url: DETAILING_IMAGE, title: 'Expert Detailing' },
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      location: 'Chitradurga',
      rating: 5,
      text: 'Excellent service! They came right to my home and did an amazing job. My car looks brand new. Highly recommended!',
    },
    {
      name: 'Priya Sharma',
      location: 'Basaweshwara Nagar',
      rating: 5,
      text: 'The convenience of doorstep service is unmatched. Professional team, premium products, and great results every time.',
    },
    {
      name: 'Anil Gowda',
      location: 'Chitradurga',
      rating: 5,
      text: 'Been using Washora for 3 months now. Consistent quality and their monthly subscription is great value for money.',
    },
    {
      name: 'Meena R.',
      location: 'Chitradurga',
      rating: 5,
      text: 'VIP membership is worth every rupee. 15-minute response time and priority service - exactly what busy professionals need.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Our Work
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Gallery & Testimonials
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            See the results of our premium car care services and hear from our happy customers.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Our Work in Action
            </h2>
            <p className="text-[#64748B]">Premium results, every time.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className="relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image.url} 
                  alt={image.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C5D]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white font-heading font-semibold p-6">{image.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              What Our Customers Say
            </h2>
            <p className="text-[#64748B]">Real reviews from real customers.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-[#F7931E] text-[#F7931E]" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-[#1E88E5]/20 mb-4" />
                <p className="text-[#64748B] mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#1E88E5]/10 rounded-full flex items-center justify-center mr-4">
                    <span className="font-heading font-bold text-[#1E88E5]">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#0B2C5D]">{testimonial.name}</p>
                    <p className="text-sm text-[#64748B]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Happy Customers' },
              { value: '1000+', label: 'Cars Washed' },
              { value: '4.9', label: 'Star Rating' },
              { value: '100%', label: 'Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-[#F7931E] mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Ready to Join Our Happy Customers?
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            Book your first doorstep car wash and experience the Washora difference.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30"
            data-testid="gallery-book-now"
          >
            Book Now <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
